import React from 'react';
import { Box, Heading } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { parseDomain } from '@wix/domains-serverless-interface';
import { AiInsightsButton, ComBrRestriction, DomainText, PremiumDomainBadge, PricingSection, isInteractiveClickTarget } from '@wix/domains-search-suite';
import { useIsMobile } from '../../hooks/useIsMobile';
import { HeroInsightBullets } from '../HeroInsightBullets';
import { HeroBusinessEmailExample } from './HeroBusinessEmailExample';
import { HeroBulletsSkeleton } from './HeroBulletsSkeleton';
import { HERO_BULLETS_PADDING_TOP, HERO_COLUMN_GAP } from './heroGeometry';
import { TldAlternativesWithPrice } from '../TldAlternativesWithPrice';
import { AvailableNowBadge } from './AvailableNowBadge';
import { Confetti } from './Confetti';
import { hasMatchLabel, MatchLabelBadge } from './MatchLabelBadge';
import { CompactHeroCard } from './CompactHeroCard';
import { HeroCtaButton } from './HeroCtaButton';
import { StackedMobileCard } from './StackedMobileCard';
import s from './HeroCard.css';
const SCALED_TITLE_MIN_FONT_SIZE = '1.25rem';
export const HeroCard = _ref => {
  let {
    recommendation,
    onClickGetIt,
    onItemClick,
    onAiInsightsClick,
    disabled = false,
    dataHook = 'hero-card',
    useCompactHeroCardLayout = false,
    desktopContentGap = '10px',
    insightsPaddingTop = 'SP2',
    compactPriceLabel = false,
    Label,
    compact = false,
    badgeLabel,
    ctaPriority = 'primary',
    inlineMobileActions = false,
    stackedMobileLayout = false,
    businessEmailExample = false,
    isPricingLoading = false,
    titleOverflowStrategy = 'ellipsis',
    PriceNote,
    priceNoteApplies
  } = _ref;
  const isMobile = useIsMobile();
  const {
    domain = '',
    label,
    premium = false,
    price
  } = recommendation;
  const {
    tld
  } = parseDomain(domain);
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  const showClickableCardStyling = !!onItemClick && !isMobile;
  const isStackedMobile = stackedMobileLayout && isMobile;
  if (compact) {
    return /*#__PURE__*/React.createElement(CompactHeroCard, {
      recommendation: recommendation,
      onClickGetIt: onClickGetIt,
      onItemClick: onItemClick,
      disabled: disabled,
      dataHook: dataHook,
      useCompactHeroCardLayout: useCompactHeroCardLayout,
      stackedMobileLayout: stackedMobileLayout,
      Label: Label,
      PriceNote: PriceNote,
      badgeLabel: badgeLabel,
      ctaPriority: ctaPriority,
      businessEmailExample: businessEmailExample,
      isPricingLoading: isPricingLoading,
      compactPriceLabel: compactPriceLabel
    });
  }
  const showMatchBadge = hasMatchLabel(label) && Boolean(label);
  const ctaButton = /*#__PURE__*/React.createElement(HeroCtaButton, {
    size: isMobile && !isStackedMobile ? 'medium' : 'large',
    priority: ctaPriority,
    disabled: disabled,
    fullWidth: isStackedMobile,
    onClick: () => onClickGetIt == null ? void 0 : onClickGetIt(recommendation),
    dataHook: `${dataHook}-cta`
  });
  const badgesRow = /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2",
    verticalAlign: "middle",
    flexWrap: "wrap"
  }, showMatchBadge && label && (Label ? /*#__PURE__*/React.createElement(Label, {
    primary: true,
    label: label,
    domain: domain,
    size: isMobile ? 'tiny' : 'small'
  }) : /*#__PURE__*/React.createElement(MatchLabelBadge, {
    label: label,
    optimized: false
  })), isExactMatch && /*#__PURE__*/React.createElement(AvailableNowBadge, null), premium && /*#__PURE__*/React.createElement(PremiumDomainBadge, {
    size: isMobile ? 'tiny' : 'small'
  }));
  const isPerception = Boolean(PriceNote);
  const scalesTitleToFit = titleOverflowStrategy === 'scale';
  const scaledTitleMaxFontSize = isStackedMobile ? '1.75rem' : isMobile ? '1.875rem' : '2rem';
  const titleHeading = useCompactHeroCardLayout ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    className: scalesTitleToFit ? s.titleColumn : undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement(DomainText, {
    overflowStrategy: titleOverflowStrategy,
    minFontSize: SCALED_TITLE_MIN_FONT_SIZE,
    maxFontSize: scaledTitleMaxFontSize,
    maxWidth: isMobile ? '310px' : '440px',
    textComponent: Heading,
    textComponentProps: {
      as: 'h1',
      size: isMobile ? 'large' : 'extraLarge'
    },
    className: `${s.optimizedHeroTitle}${isPerception ? ` ${s.perceptionHeroTitle}` : ''}${isStackedMobile ? ` ${s.stackedMobileTitle}` : ''}`,
    dataHook: `${dataHook}-title`
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)), isExactMatch && /*#__PURE__*/React.createElement(ComBrRestriction, {
    domainName: domain
  })) : /*#__PURE__*/React.createElement(Heading, {
    size: isMobile ? 'large' : 'extraLarge',
    as: "h1",
    dataHook: `${dataHook}-title`
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: isMobile ? 600 : 500
    }
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)));
  const LeadingBullet = PriceNote && priceNoteApplies != null && priceNoteApplies(price, domain) ? () => /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain,
    bulletAligned: true
  }) : undefined;
  const pricingSection = /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    useMobileLayout: isMobile,
    compactPriceLabel: compactPriceLabel,
    useIntroPriceLabelCopy: compactPriceLabel && useCompactHeroCardLayout,
    denseStrikethroughStyle: useCompactHeroCardLayout,
    matchStrikethroughSize: isMobile && useCompactHeroCardLayout,
    isPricingLoading: isPricingLoading
  });
  const stackedMobileSection = /*#__PURE__*/React.createElement(StackedMobileCard, {
    spacious: isExactMatch,
    badges: badgesRow,
    title: titleHeading,
    bullets: isExactMatch && /*#__PURE__*/React.createElement(HeroInsightBullets, {
      domain: domain,
      size: "small"
    }),
    pricing: pricingSection,
    cta: ctaButton
  });
  const topSection = /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    padding: isMobile ? 'SP6 SP6 16px SP6' : 'SP6',
    align: isMobile ? undefined : 'space-between',
    gap: isMobile ? useCompactHeroCardLayout ? '16px' : '10px' : 'SP6'
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? useCompactHeroCardLayout ? '16px' : 'SP6' : isPerception ? HERO_COLUMN_GAP : desktopContentGap,
    flex: isMobile ? undefined : 1,
    minWidth: 0
  }, badgesRow, businessEmailExample ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px"
  }, titleHeading, !isMobile && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, titleHeading, isMobile ? PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain
  }) : isExactMatch && /*#__PURE__*/React.createElement(Box, {
    paddingTop: isPerception || LeadingBullet ? HERO_BULLETS_PADDING_TOP : insightsPaddingTop
  }, isPerception && isPricingLoading ? /*#__PURE__*/React.createElement(HeroBulletsSkeleton, null) : /*#__PURE__*/React.createElement(HeroInsightBullets, {
    domain: domain,
    LeadingBullet: LeadingBullet,
    singleLineRows: isPerception,
    iconColor: isPerception ? 'var(--wds-color-text-standard-primary)' : undefined
  })))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP4",
    verticalAlign: "middle",
    flexShrink: 0,
    width: isMobile ? '100%' : undefined,
    align: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'space-between' : undefined,
    flexWrap: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'wrap' : undefined,
    alignSelf: isMobile ? undefined : 'flex-end'
  }, pricingSection, (!isMobile || useCompactHeroCardLayout || inlineMobileActions) && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    marginLeft: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'auto' : undefined
  }, ctaButton)), isMobile && !useCompactHeroCardLayout && !inlineMobileActions && /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle"
  }, onAiInsightsClick ? /*#__PURE__*/React.createElement(AiInsightsButton, {
    onClick: () => onAiInsightsClick(domain)
  }) : /*#__PURE__*/React.createElement(Box, null), ctaButton));
  const cardSection = isStackedMobile ? stackedMobileSection : topSection;
  const clickableCardSection = onItemClick ? /*#__PURE__*/React.createElement("div", {
    className: s.cardClickArea,
    "data-hook": `${dataHook}-click-area`,
    onClick: event => {
      if (disabled || isInteractiveClickTarget(event.target)) {
        return;
      }
      onItemClick(recommendation);
    }
  }, cardSection) : cardSection;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    className: [useCompactHeroCardLayout ? s.card : s.cardShadow, showClickableCardStyling && s.clickableCard].filter(Boolean).join(' '),
    dataHook: dataHook
  }, clickableCardSection, !isExactMatch && /*#__PURE__*/React.createElement(TldAlternativesWithPrice, {
    heroDomain: domain,
    disabled: disabled,
    withDivider: true,
    containerProps: {
      padding: 'SP4 SP6 SP6 SP6',
      className: s.TldAlternatives
    }
  }));
};
//# sourceMappingURL=HeroCard.js.map